import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { ClaudePromptPanel } from '@/components/admin/npc/launch/claude-prompt-panel';
import { SeriesRunsTable } from '@/components/admin/npc/series/series-runs-table';
import { SeriesStopDialog } from '@/components/admin/npc/series/series-stop-dialog';
import { Alert, Button, Spinner } from '@pms-digital/design-system';

import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, fetchNpcSeriesDetail, relaunchNpcSeries, stopNpcSeries } from '@/lib/api';
import { seriesRefusalSentence } from '@/lib/npc-launch';
import { seriesStateLabel, seriesStateTone } from '@/lib/npc-replays-labels';
import {
  canStopSeries,
  relaunchLabel,
  relaunchableRuns,
  restartBannerTitle,
  seriesHeadFacts,
  seriesLaunchedBy,
  seriesTitle,
  seriesTotals,
  stopEffect,
} from '@/lib/npc-series-page';
import type { NpcSeriesDetail } from '@matter_extractor/shared';
import { NPC_WATCH_POLL_MS } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * La page d'une série (npc.md §9 ; maquette `Serie`) : le fil d'Ariane, l'en-tête - banc ou NPC,
 * expérience et version, côté, mode, joueur, qui l'a lancée -, son état, ses totaux (rejeux finis,
 * coût du dispositif et du joueur, l'estimation faite au lancement), ses gestes, puis le tableau
 * d'une ligne par rejeu.
 *
 * ## Elle se relit toute seule tant que la série n'est pas finie
 *
 * La série est lue toutes les `NPC_WATCH_POLL_MS` tant qu'elle tourne ou qu'elle attend Claude : les
 * rangs de la file bougent, un tour avance, un rejeu finit, et le serveur est la seule source de
 * tout cela. Finie, arrêtée ou interrompue, elle ne se relit plus : elle ne changera pas sans un
 * geste, et ce geste écrit lui-même sa réponse dans le cache.
 *
 * ## Les gestes
 *
 * « Arrêter la série » ouvre une boîte qui montre l'effet ; le POST peut durer jusqu'à trente
 * secondes, la boîte le dit et reste ouverte. « Relancer les rejeux interrompus » n'a pas de boîte :
 * il ne détruit rien, il ajoute des rejeux neufs à la même série, et le bandeau du redémarrage
 * (§7.3) est là, à côté, pour dire pourquoi. Les refus se disent depuis leur `code`.
 *
 * Aucun écran de cette page ne compare (N10) : ni cette série à une autre, ni le noyau retenu à un
 * noyau attendu.
 */

export interface NpcSeriesPageProps {
  seriesId: string;
}

export function NpcSeriesPage({ seriesId }: NpcSeriesPageProps) {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  const key = ['npc-series-detail', email, seriesId] as const;
  // The effect is read ONCE, when the box opens: the page keeps polling while the stop runs (up to
  // 30 s), and the counts the operator is reading must not move under the sentence.
  const [stopping, setStopping] = useState<ReturnType<typeof stopEffect> | null>(null);

  const series = useQuery({
    queryKey: key,
    queryFn: () => fetchNpcSeriesDetail(seriesId),
    retry: false,
    refetchInterval: (query) => {
      // A read that failed stops the polling (a series that is gone answers 404 for good).
      if (query.state.status === 'error') return false;
      const state = query.state.data?.state;
      return state === 'running' || state === 'waiting-claude' ? NPC_WATCH_POLL_MS : false;
    },
  });
  const rejected = useRejectOn401(series.error);
  const missing = series.error instanceof ApiError && series.error.status === 404;

  const settle = (next: NpcSeriesDetail) => {
    queryClient.setQueryData(key, next);
    void queryClient.invalidateQueries({ queryKey: ['npc-series', email] });
    void queryClient.invalidateQueries({ queryKey: ['npc-replays'] });
  };
  const stop = useMutation({
    mutationFn: () => stopNpcSeries(seriesId),
    onSuccess: (next) => {
      settle(next);
      setStopping(null);
    },
  });
  const relaunch = useMutation({
    mutationFn: () => relaunchNpcSeries(seriesId),
    onSuccess: settle,
  });

  const data = series.data;

  return (
    <section data-testid="npc-serie" className="space-y-5 pb-16">
      <p className="text-gray-600 text-small">
        <Link to="/admin/npc" className="text-bleu-nuit underline underline-offset-2">
          NPC
        </Link>
        <span className="mx-2">›</span>
        <Link
          to="/admin/npc"
          search={{ onglet: 'series' }}
          className="text-bleu-nuit underline underline-offset-2"
        >
          Séries
        </Link>
        <span className="mx-2">›</span>
        {data === undefined ? '...' : seriesTitle(data)}
      </p>

      {series.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner label="Chargement de la série" />
        </div>
      ) : null}

      {missing ? (
        <p data-testid="npc-serie-introuvable" className="text-gray-700">
          Cette série n’existe pas.{' '}
          <Link
            to="/admin/npc"
            search={{ onglet: 'series' }}
            className="text-bleu-nuit underline underline-offset-2"
          >
            Revenir aux séries
          </Link>
          .
        </p>
      ) : null}
      {series.isError && !missing && !rejected ? (
        <p data-testid="npc-serie-erreur" className="text-gray-700">
          La série n’a pas pu être chargée.
        </p>
      ) : null}

      {data === undefined ? null : (
        <>
          <SeriesHead
            series={data}
            onStop={() => {
              stop.reset();
              setStopping(stopEffect(data));
            }}
          />

          {data.restart === null ? null : (
            <div data-testid="npc-serie-redemarrage">
              <Alert tone="warning" title={restartBannerTitle(data.restart)}>
                <p>
                  Un rejeu joué par le serveur s’arrête au redémarrage : il ne reprend pas seul. Les
                  rejeux qui attendaient n’avaient pas commencé ; ils repartent dans l’ordre, dès
                  que le serveur est revenu.
                </p>
              </Alert>
            </div>
          )}

          <Relaunch
            series={data}
            busy={relaunch.isPending}
            error={
              relaunch.isError
                ? (seriesRefusalSentence(relaunch.error) ??
                  'La relance a échoué. Réessayez dans un instant.')
                : null
            }
            onRelaunch={() => {
              relaunch.reset();
              relaunch.mutate();
            }}
          />

          {data.claudePrompt === null ? null : (
            <div data-testid="npc-serie-claude" className="space-y-2">
              <h2 className="font-semibold text-gray-900 text-small">
                La phrase à coller dans Claude Code ou claude.ai, avec le connecteur du dev
              </h2>
              <ClaudePromptPanel prompt={data.claudePrompt} testId="npc-serie" />
            </div>
          )}

          <SeriesRunsTable series={data} />

          {data.state === 'running' || data.state === 'waiting-claude' ? (
            <p className="text-caption text-gray-600">
              La page se rafraîchit seule tant que la série tourne. Huit rejeux joués par le serveur
              à la fois sur tout le dev : les autres attendent leur tour, dans l’ordre de lancement.
            </p>
          ) : null}

          {stopping !== null ? (
            <SeriesStopDialog
              {...stopping}
              claude={data.player.kind === 'claude'}
              busy={stop.isPending}
              error={
                stop.isError
                  ? (seriesRefusalSentence(stop.error) ??
                    'L’arrêt a échoué. Réessayez dans un instant.')
                  : null
              }
              onConfirm={() => stop.mutate()}
              onCancel={() => setStopping(null)}
            />
          ) : null}
        </>
      )}
    </section>
  );
}

function SeriesHead({ series, onStop }: { series: NpcSeriesDetail; onStop: () => void }) {
  const totals = seriesTotals(series);
  const target = series.target;
  const slots = `${series.passes} ${series.passes > 1 ? 'passes' : 'passe'}`;
  return (
    <header className="space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 data-testid="npc-serie-titre" className="font-bold text-bleu-nuit text-h3">
              {seriesTitle(series)}
            </h1>
            <StatusBadge
              tone={seriesStateTone(series.state)}
              pulse={series.state === 'running'}
              data-testid="npc-serie-etat"
            >
              {seriesStateLabel(series)}
            </StatusBadge>
            {series.label === null ? null : (
              <span
                data-testid="npc-serie-etiquette"
                className="rounded-sm border border-gray-300 bg-white px-1.5 py-0.5 text-caption text-gray-700"
              >
                {series.label}
              </span>
            )}
          </div>
          <p data-testid="npc-serie-faits" className="text-gray-700 text-small">
            {target.kind === 'bench' ? (
              <>
                Banc{' '}
                <Link
                  to="/admin/npc"
                  search={{ onglet: 'bancs', banc: target.benchId }}
                  className="text-bleu-nuit underline underline-offset-2"
                >
                  {target.benchName}
                </Link>{' '}
                ({Math.round(series.slotCount / Math.max(1, series.passes))} NPC, {slots}) ·{' '}
              </>
            ) : (
              <>
                NPC{' '}
                <Link
                  to="/admin/npc/$npcId"
                  params={{ npcId: target.npcId }}
                  className="text-bleu-nuit underline underline-offset-2"
                >
                  {target.npcName}
                </Link>{' '}
                ({slots}) ·{' '}
              </>
            )}
            {seriesHeadFacts(series)} · {seriesLaunchedBy(series)}
            {series.stoppedAt === null
              ? ''
              : ` · arrêtée${series.stoppedBy === null ? '' : ` par ${series.stoppedBy}`}`}
          </p>
          <p data-testid="npc-serie-totaux" className="text-gray-800 text-small">
            Rejeux finis <b className="font-semibold">{totals.finished}</b> · {totals.cost}
            {totals.estimate === null ? '' : ` · ${totals.estimate}`}
          </p>
        </div>
        {canStopSeries(series) ? (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            compact
            data-testid="npc-serie-arreter"
            onClick={onStop}
          >
            Arrêter la série
          </Button>
        ) : null}
      </div>
    </header>
  );
}

function Relaunch({
  series,
  busy,
  error,
  onRelaunch,
}: {
  series: NpcSeriesDetail;
  busy: boolean;
  error: string | null;
  onRelaunch: () => void;
}) {
  const interrupted = relaunchableRuns(series);
  if (interrupted.length === 0 && error === null) return null;
  return (
    <div className="space-y-2">
      {interrupted.length === 0 ? null : (
        <Button
          type="button"
          variant="accent"
          size="sm"
          compact
          data-testid="npc-serie-relancer"
          disabled={busy}
          onClick={onRelaunch}
        >
          {busy ? 'Relance en cours...' : relaunchLabel(interrupted.length)}
        </Button>
      )}
      <p
        aria-live="polite"
        data-testid="npc-serie-relance-refus"
        className="text-rouge-deep text-small"
      >
        {error}
      </p>
    </div>
  );
}
